// src/hooks/useTutorial.js
import { useState, useEffect } from "react";
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  introDialogue,
  battleTutorial,
  firstDeck,
} from "./tutorialSequenceSteps";

/**
 * Hooks into users/{uid}.tutorialProgress and tutorialSequences/{sequenceId}.
 */
export function useTutorial(uid) {
  const [progress, setProgress] = useState(null);
  const [sequence, setSequence] = useState(null);

  // 1) Listen to the user doc and seed tutorialProgress if missing
  useEffect(() => {
    if (!uid) return;
    const userRef = doc(db, "users", uid);

    const unsubUser = onSnapshot(userRef, async (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();

      // If no tutorialProgress yet, initialize it
      if (!data.tutorialProgress) {
        await updateDoc(userRef, {
          tutorialProgress: {
            currentSequence: "introDialogue",
            currentStepIndex: 0,
            completedSequences: [],
            updatedAt: serverTimestamp(),
          },
        });
        return; // wait for the next snapshot with the seeded data
      }

      setProgress(data.tutorialProgress);
    });

    return unsubUser;
  }, [uid]);

  // 2) Once we know the currentSequence, listen to that sequence doc
  useEffect(() => {
    if (!progress?.currentSequence) return;
    if (progress.currentSequence === "introDialogue") {
      setSequence(introDialogue);
      return;
    }
    if (progress.currentSequence === "guidedBattle") {
      setSequence(battleTutorial);
      return;
    }
    if (progress.currentSequence === "firstDeck") {
      setSequence(firstDeck);
      return;
    }
  }, [progress?.currentSequence]);

  // 3) Advance either to the next step or wrap up the sequence
  const advanceStep = async () => {
    if (!progress || !sequence) return;
    console.log("advanceStep", progress, sequence);
    const userRef = doc(db, "users", uid);
    const {
      currentSequence,
      currentStepIndex,
      completedSequences = [],
    } = progress;

    if (sequence.steps && currentStepIndex + 1 < sequence.steps.length) {
      // Move to next step in the same sequence
      await updateDoc(userRef, {
        "tutorialProgress.currentStepIndex": currentStepIndex + 1,
        "tutorialProgress.updatedAt": serverTimestamp(),
      });
    } else {
      // Finish this sequence, jump to the next one
      await updateDoc(userRef, {
        "tutorialProgress.currentSequence": sequence.nextSequence,
        "tutorialProgress.currentStepIndex": 0,
        "tutorialProgress.completedSequences": [
          ...completedSequences,
          currentSequence,
        ],
        "tutorialProgress.updatedAt": serverTimestamp(),
      });
    }
  };

  return { progress, sequence, advanceStep };
}
